<template>
  <div data-testid="project-settings-team">
    <p
      v-if="linkedTeams.length"
      class="text-[13px] text-[#6B6B6B] mb-3 m-0"
    >
      {{ $t('UserProjects.settings.team.linkedTeams', { teams: linkedTeams.join(', ') }) }}
    </p>

    <div class="divide-y divide-[#E5E5E5]">
      <div
        v-for="member in members"
        :key="member.userId"
        class="flex items-center gap-3 py-3"
        data-testid="project-settings-member-row"
      >
        <div
          class="w-8 h-8 rounded-full flex items-center justify-center text-white text-[12px] font-bold shrink-0 bg-[var(--color-orange,#e4821a)]"
        >
          {{ initials(member) }}
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-1.5">
            <p class="text-[14px] font-semibold m-0 truncate text-[var(--color-dark,#1A1A1A)]">
              {{ member.name || member.email || member.userId }}
            </p>
            <span v-if="member.isMe" class="text-[12px] text-[#ABABAB]">
              {{ $t('UserProjects.settings.team.me') }}
            </span>
          </div>
          <p class="text-[12px] text-[#6B6B6B] m-0 truncate">{{ member.email }}</p>
        </div>
        <span
          class="text-[10px] font-medium px-2 py-1 rounded-full shrink-0"
          :style="roleBadgeStyle(member.role)"
        >
          {{ $t(`UserProjects.settings.roles.${member.role}`) }}
        </span>
        <div v-if="canManage && !member.isMe" class="flex items-center gap-3 shrink-0">
          <button
            type="button"
            class="text-[12px] font-medium text-[#6B6B6B] hover:text-[var(--color-dark,#1A1A1A)] transition-colors bg-transparent border-0 p-0 cursor-pointer"
            :disabled="busy"
            data-testid="project-settings-change-role"
            @click="openRoleMenu($event, member)"
          >
            {{ $t('UserProjects.settings.team.changeRole') }}
          </button>
          <button
            type="button"
            class="text-[12px] font-medium text-[#EF4444] hover:opacity-70 transition-opacity bg-transparent border-0 p-0 cursor-pointer"
            :disabled="busy"
            data-testid="project-settings-remove-member"
            @click="$emit('remove', member.userId)"
          >
            {{ $t('UserProjects.settings.team.remove') }}
          </button>
        </div>
      </div>
    </div>

    <Menu
      ref="roleMenu"
      :model="roleMenuItems"
      popup
      data-testid="project-settings-role-menu"
    />

    <div v-if="canManage" class="border-t border-[#E5E5E5] mt-4 pt-4">
      <p class="text-[14px] font-semibold mb-3 m-0 text-[#1A1A1A]">
        {{ $t('UserProjects.settings.team.inviteTitle') }}
      </p>
      <div class="project-settings-invite-row">
        <div class="project-settings-invite-row__users">
          <UserMultiSelectInput
            v-model="inviteUserIds"
            :users="availableUsers"
            :placeholder="$t('UserProjects.settings.team.invitePlaceholder')"
            :disabled="busy"
          />
        </div>
        <Select
          v-model="inviteRole"
          :options="inviteRoleOptions"
          option-label="label"
          option-value="value"
          class="project-settings-field project-settings-invite-row__role"
          :disabled="busy"
          data-testid="project-settings-invite-role"
        />
        <button
          type="button"
          class="project-settings-invite-row__btn"
          :disabled="!inviteUserIds.length || busy"
          data-testid="project-settings-invite"
          @click="invite"
        >
          <i v-if="busy" class="pi pi-spin pi-spinner mr-1" aria-hidden="true" />
          {{ $t('UserProjects.settings.team.invite') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Menu from 'primevue/menu';
import Select from 'primevue/select';
import UserMultiSelectInput from '@/components/global/UserMultiSelectInput.vue';

export default {
  name: 'ProjectSettingsTeamTab',
  components: { Menu, Select, UserMultiSelectInput },
  props: {
    members: { type: Array, default: () => [] },
    canManage: { type: Boolean, default: false },
    linkedTeams: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    busy: { type: Boolean, default: false },
  },
  emits: ['add', 'remove', 'change-role'],
  data() {
    return {
      inviteUserIds: [],
      inviteRole: 'member',
      roleMenuMemberId: null,
    };
  },
  computed: {
    roleOptions() {
      return ['admin', 'project_lead', 'member', 'read_only'].map((value) => ({
        value,
        label: this.$t(`UserProjects.settings.roleLabels.${value}`),
      }));
    },
    inviteRoleOptions() {
      return this.roleOptions;
    },
    roleMenuItems() {
      return this.roleOptions.map((option) => ({
        label: option.label,
        command: () => {
          if (!this.roleMenuMemberId) return;
          this.$emit('change-role', {
            userId: this.roleMenuMemberId,
            role: option.value,
          });
        },
      }));
    },
    availableUsers() {
      const existing = new Set((this.members || []).map((m) => String(m.userId)));
      return (this.users || []).filter((u) => !existing.has(String(u.id)));
    },
  },
  methods: {
    initials(member) {
      const name = member.name || member.email || '?';
      return name
        .split(/\s+/)
        .map((p) => p[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();
    },
    roleBadgeStyle(role) {
      if (role === 'admin') {
        return { backgroundColor: '#FDF0E6', color: '#e4821a' };
      }
      return { backgroundColor: '#F2F2F2', color: '#6B6B6B' };
    },
    openRoleMenu(event, member) {
      this.roleMenuMemberId = member.userId;
      this.$refs.roleMenu.toggle(event);
    },
    invite() {
      if (!this.inviteUserIds.length) return;
      this.$emit('add', {
        userIds: [...this.inviteUserIds],
        role: this.inviteRole,
      });
      this.inviteUserIds = [];
      this.inviteRole = 'member';
    },
  },
};
</script>

<style scoped>
.project-settings-invite-row {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.5rem;
}

.project-settings-invite-row__users {
  flex: 1 1 55%;
  min-width: 12rem;
}

.project-settings-invite-row__role {
  flex: 0 1 25%;
  min-width: 7.5rem;
  max-width: 10rem;
}

.project-settings-invite-row__btn {
  flex: 0 0 auto;
  min-width: 5.5rem;
  height: 40px;
  padding: 0 0.75rem;
  border: none;
  border-radius: 10px;
  background: var(--accent-gradient);
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.project-settings-invite-row__btn:hover:not(:disabled) {
  opacity: 0.92;
}

.project-settings-invite-row__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>

<style>
.project-settings-invite-row__users .p-multiselect {
  width: 100%;
  min-height: 40px;
  border: 1.5px solid #e5e5e5;
  border-radius: 10px;
  box-shadow: none;
}

.project-settings-invite-row__users .p-multiselect.p-focus {
  border-color: #e4821a;
  box-shadow: 0 0 0 2px rgba(228, 130, 26, 0.2);
}

.project-settings-invite-row__role.p-select {
  width: 100%;
  height: 40px;
  border: 1.5px solid #e5e5e5;
  border-radius: 10px;
  box-shadow: none;
}

.project-settings-invite-row__role.p-select.p-focus {
  border-color: #e4821a;
  box-shadow: 0 0 0 2px rgba(228, 130, 26, 0.2);
}

.project-settings-invite-row__role.p-select .p-select-label {
  padding: 0.5rem 0.5rem;
  font-size: 13px;
  line-height: 1.4;
}
</style>
